import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Radio,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
  Chip,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Store as StoreIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Rocket as RocketIcon,
  CheckCircle as CheckCircleIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Security as SecurityIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const palette = {
  primary: '#1F3A5F',
  secondary: '#2C8C8C',
  accent: '#C49A3C',
  bg: '#F8F8F6',
  bgDark: '#0F1E2E',
  bgCard: '#FFFFFF',
  textDark: '#111827',
  textMuted: '#4B5563',
  border: '#E5E7EB',
};

const TEMPLATE_DEFS = [
  { id: 'classic', image: '/images/landing/template-classic.webp' },
  { id: 'fashion', image: '/images/landing/template-fashion.webp' },
  { id: 'handmade', image: '/images/landing/template-handmade.webp' },
  { id: 'food', image: '/images/landing/template-food.webp' },
  { id: 'tech', image: '/images/landing/template-tech.webp' },
];

// PLAN_INFO and steps moved inside component to access t()

function Apply() {
  const { t } = useTranslation('auth');

  // 兩步完成：先確認聯絡方式，再建立商店
  const steps = [t('apply.step1_title'), t('apply.step2_title')];
  const navigate = useNavigate();
  const location = useLocation();

  const PLAN_OPTIONS = [
    {
      id: 2,
      code: 'basic',
      name: t('apply.plan_basic_name', '創業方案'),
      badge: '熱門首選・方案A',
      badgeColor: '#C49A3C',
      yearlyPrice: 6999,
      monthlyAvg: 583,
      desc: '適合獨立品牌、團購主、好批進貨客群起步',
      features: ['商品數 100 件', '每月 300 筆訂單', '全功能金物流串接', '好批商品一鍵鋪貨']
    },
    {
      id: 3,
      code: 'pro',
      name: t('apply.plan_pro_name', '成長方案'),
      badge: '深度行銷',
      badgeColor: '#2C8C8C',
      yearlyPrice: 14999,
      monthlyAvg: 1250,
      desc: '適合訂單成長中的中小型電商品牌',
      features: ['商品數 500 件', '每月 1,000 筆訂單', '自訂獨立網域', 'LINE 智慧再行銷']
    },
    {
      id: 4,
      code: 'enterprise',
      name: t('apply.plan_enterprise_name', '品牌方案'),
      badge: '旗艦規格',
      badgeColor: '#1F3A5F',
      yearlyPrice: 29999,
      monthlyAvg: 2500,
      desc: '適合高營業額、大型品牌或客製整合需求',
      features: ['無限商品與訂單', '專屬客服顧問', '專屬客製化功能', '優先伺服器資源']
    },
    {
      id: 1,
      code: 'free',
      name: t('apply.plan_free_name', '免費試用'),
      badge: '免綁卡立即體驗',
      badgeColor: '#6B7280',
      yearlyPrice: 0,
      monthlyAvg: 0,
      desc: '14 天免費完整體驗，無須預先付款',
      features: ['14 天免費體驗', '基本開店核心功能', '支援線上測試下單', '隨時可升級正式版']
    }
  ];

  // Landing page links to /apply?plan={free|basic|pro|enterprise}. Backend expects numeric planId.
  const selectedPlanId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const code = (params.get('plan') || '').toLowerCase();
    const map = { free: 1, basic: 2, pro: 3, enterprise: 4 };
    return map[code] || 2; // 預設方案A：創業方案
  }, [location.search]);

  const [chosenPlanId, setChosenPlanId] = useState(selectedPlanId);
  const [paymentType, setPaymentType] = useState('credit_card');
  const [redirectingToSmilePay, setRedirectingToSmilePay] = useState(false);

  useEffect(() => {
    if (selectedPlanId) {
      setChosenPlanId(selectedPlanId);
    }
  }, [selectedPlanId]);

  const selectedTemplateId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const tpl = String(params.get('template') || '').toLowerCase();
    const allowed = new Set(['classic', 'fashion', 'handmade', 'food', 'tech']);
    return allowed.has(tpl) ? tpl : null;
  }, [location.search]);

  // Activation code support (EZP purchase → SAAS activation)
  const activationCodeFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('activation') || '';
  }, [location.search]);

  const [activationInfo, setActivationInfo] = useState(null);
  const [activationLoading, setActivationLoading] = useState(false);
  const [activationError, setActivationError] = useState('');

  useEffect(() => {
    if (!activationCodeFromUrl) return;
    setActivationLoading(true);
    api.get(`/apply/validate-activation/${activationCodeFromUrl}`)
      .then(res => {
        if (res.data.valid) {
          setActivationInfo(res.data);
          setActivationError('');
        } else {
          setActivationError(res.data.reason || '開通碼無效');
        }
      })
      .catch(() => setActivationError('開通碼驗證失敗'))
      .finally(() => setActivationLoading(false));
  }, [activationCodeFromUrl]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [templateChoice, setTemplateChoice] = useState(selectedTemplateId || 'classic');
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  useEffect(() => {
    if (selectedTemplateId) setTemplateChoice(selectedTemplateId);
  }, [selectedTemplateId]);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [authConfig, setAuthConfig] = useState({
    google_enabled: false,
    google_client_id: ''
  });
  const [googleReady, setGoogleReady] = useState(false);
  const [subdomainStatus, setSubdomainStatus] = useState({ checking: false, available: null, reason: '' });
  const [error, setError] = useState('');

  // Stepper labels (UI).
  const [formData, setFormData] = useState({
    email: '',
    applicantName: '',
    platformName: '',
    subdomain: '',
    agreedTerms: false
  });

  // Plans shown on this page.
  useEffect(() => {
    const checkSubdomain = async () => {
      if (!formData.subdomain || formData.subdomain.length < 3) {
        setSubdomainStatus({ checking: false, available: null, reason: '' });
        return;
      }

      setSubdomainStatus({ checking: true, available: null, reason: '' });

      try {
        const response = await api.get(`/apply/check-subdomain/${formData.subdomain}`);
        setSubdomainStatus({
          checking: false,
          available: response.data.available,
          reason: response.data.reason || ''
        });
      } catch (err) {
        setSubdomainStatus({
          checking: false,
          available: false,
          reason: t('apply.slug_check_failed')
        });
      }
    };

    const timer = setTimeout(checkSubdomain, 500);
    return () => clearTimeout(timer);
  }, [formData.subdomain]);

  const [subdomainHint, setSubdomainHint] = useState('');

  // Auto-suggest subdomain from store name.
  useEffect(() => {
    if (formData.platformName && !formData.subdomain) {
      const suggested = formData.platformName
        .toLowerCase()
        .replace(/[^a-z0-9\u4e00-\u9fa5]/g, '')
        .slice(0, 20);

      const english = suggested.replace(/[\u4e00-\u9fa5]/g, '');
      if (english.length >= 3) {
        setFormData(prev => ({ ...prev, subdomain: english }));
        setSubdomainHint('');
      } else if (formData.platformName.length >= 2) {
        // Chinese name detected, provide helpful hint
        setSubdomainHint(t('apply.slug_chinese_hint'));
      }
    }
  }, [formData.platformName]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubdomainChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setFormData(prev => ({ ...prev, subdomain: value }));
  };

  const googleInitialized = useRef(false);
  const googleCallbackRef = useRef(null);
  const googleButtonRef = useRef(null);

  // Avoid stale closures by using a ref for Google callback.
  googleCallbackRef.current = async (response) => {
    console.log('[Apply] Google callback fired, credential:', !!response.credential);
    if (!response.credential) {
      console.warn('[Apply] Google callback: no credential received');
      setError(t('login.google_login_failed'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('[Apply] Calling /auth/google API...');
      const result = await api.post('/auth/google', { token: response.credential });
      console.log('[Apply] /auth/google response:', result.data?.success);

      if (result.data.success) {
        const { user } = result.data;
        setFormData(prev => ({
          ...prev,
          email: user.email || '',
          applicantName: user.name || prev.applicantName || '',
        }));
        setActiveStep(1);
      }
    } catch (err) {
      console.error('[Apply] Google login error:', err.response?.status, err.response?.data);
      setError(err.response?.data?.error || t('login.google_login_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const loadAuthConfig = async () => {
      try {
        const res = await api.get('/settings/auth/public');
        if (mounted && res.data?.success) {
          setAuthConfig({
            google_enabled: !!res.data.config?.google_enabled,
            google_client_id: res.data.config?.google_client_id || ''
          });
        }
      } catch (err) {
        // Keep email flow available even if auth settings endpoint fails.
      }
    };

    loadAuthConfig();
    return () => {
      mounted = false;
    };
  }, []);

  // Initialize Google Identity Services with hidden renderButton (popup OAuth flow).
  useEffect(() => {
    const googleClientId = authConfig.google_client_id || process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
    if (!authConfig.google_enabled || !googleClientId || googleInitialized.current) return;

    const initializeGoogle = () => {
      if (window.google?.accounts?.id) {
        try {
          console.log('[Apply] Initializing Google Identity Services, client_id:', googleClientId?.substring(0, 20) + '...');
          window.google.accounts.id.initialize({
            client_id: googleClientId,
            callback: (resp) => {
              console.log('[Apply] GIS callback invoked');
              googleCallbackRef.current?.(resp);
            },
            auto_select: false,
            ux_mode: 'popup',
          });
          googleInitialized.current = true;

          // Render hidden Google button (used to trigger popup OAuth)
          if (googleButtonRef.current) {
            window.google.accounts.id.renderButton(googleButtonRef.current, {
              type: 'standard',
              theme: 'outline',
              size: 'large',
              text: 'signup_with',
              shape: 'rectangular',
              width: 300,
            });
            setGoogleReady(true);
            console.log('[Apply] Google button rendered, ready');
          }
        } catch (e) {
          console.error('[Apply] Google init error:', e);
          setGoogleReady(false);
        }
      }
    };

    if (window.google?.accounts?.id) {
      initializeGoogle();
      return;
    }

    const existingScript = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogle;
      document.head.appendChild(script);
    } else {
      existingScript.addEventListener('load', initializeGoogle);
    }
  }, [authConfig.google_enabled, authConfig.google_client_id]);

  const handleGoogleSignIn = () => {
    if (!authConfig.google_enabled || !googleReady) {
      setError(t('login.google_not_ready'));
      return;
    }
    const btn = googleButtonRef.current?.querySelector('[role="button"]');
    if (btn) {
      btn.click();
    } else {
      setError(t('login.google_not_ready'));
    }
  };

  const setTemplateInUrl = (nextTemplateId) => {
    const params = new URLSearchParams(location.search);
    params.set('template', nextTemplateId);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  };

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const activePlanId = activationInfo ? activationInfo.plan_id : chosenPlanId;
      const payload = {
        platformName: formData.platformName,
        subdomain: formData.subdomain,
        planId: activePlanId,
        billingCycle: 'yearly',
        paymentType: paymentType,
        templateId: templateChoice || undefined,
        applicantName: formData.applicantName || formData.platformName,
        applicantEmail: formData.email,
        agreedTerms: formData.agreedTerms
      };
      if (activationCodeFromUrl && activationInfo) {
        payload.activationCode = activationCodeFromUrl;
      }
      const response = await api.post('/apply', payload);

      if (response.data.success) {
        const resData = response.data.data;
        if (resData.paymentRequired) {
          if (resData.paymentType === 'credit_card' && resData.formAction && resData.formData) {
            setRedirectingToSmilePay(true);
            // 建立隱藏 form 並 POST 提交到 SmilePay 信用卡結帳閘道（昀美收款碼 4812）
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = resData.formAction;
            Object.keys(resData.formData).forEach(key => {
              const input = document.createElement('input');
              input.type = 'hidden';
              input.name = key;
              input.value = resData.formData[key];
              form.appendChild(input);
            });
            document.body.appendChild(form);
            form.submit();
            return;
          } else {
            // ATM 轉帳付款
            navigate(`/apply/status/${resData.applicationId}`);
            return;
          }
        } else {
          // 免費試用或開通碼
          navigate(`/apply/status/${resData.applicationId}`);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || t('apply.error'));
      setRedirectingToSmilePay(false);
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep1 = () => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
  };

  const canProceedStep2 = () => {
    return formData.platformName.length >= 2 &&
           formData.subdomain.length >= 3 &&
           subdomainStatus.available === true &&
           formData.agreedTerms;
  };

  // Conversion: show short "why" bullets near the top.
  const freeFeatures = t('apply.trust_features', { returnObjects: true }) || [];

  return (
    <Box sx={{ bgcolor: palette.bg, minHeight: '100vh', position: 'relative' }}>
      {/* Top gradient color bar */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${palette.primary}, ${palette.accent}, ${palette.secondary})`,
          zIndex: 10,
        }}
      />

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            border: `1px solid ${palette.border}`,
            boxShadow: '0 12px 40px rgba(0,0,0,0.06)',
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{
              display: 'inline-flex',
              p: 2,
              borderRadius: '50%',
              bgcolor: `${palette.accent}18`,
              mb: 2
            }}>
              <RocketIcon sx={{ fontSize: 40, color: palette.accent }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 900, color: palette.textDark, letterSpacing: '-0.02em' }} gutterBottom>
              {t('apply.title')}
            </Typography>
            <Typography sx={{ color: palette.textMuted, lineHeight: 1.8, fontSize: '0.95rem' }}>
              {t('apply.subtitle')}
              <br />
              {t('apply.subtitle_detail')}
            </Typography>

            {/* Trust bullets */}
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 2, justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}
            >
              {freeFeatures.map((feature, index) => (
                <Chip
                  key={index}
                  icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                  label={feature}
                  size="small"
                  sx={{
                    bgcolor: `${palette.secondary}14`,
                    color: palette.secondary,
                    border: `1px solid ${palette.secondary}30`,
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: palette.secondary },
                  }}
                />
              ))}
            </Stack>
          </Box>

          {/* Activation code banner */}
          {activationLoading && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <CircularProgress size={16} sx={{ mr: 1 }} /> 正在驗證開通碼...
            </Alert>
          )}
          {activationError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {activationError}
            </Alert>
          )}
          {activationInfo && (
            <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircleIcon />}>
              <Typography sx={{ fontWeight: 700 }}>
                已驗證開通碼 — 您已購買「{activationInfo.plan_name}」{activationInfo.billing_cycle === 'yearly' ? '年繳' : '月繳'}方案
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                開通後立即啟用 {activationInfo.duration_months} 個月，無需試用期
              </Typography>
            </Alert>
          )}

          {/* Stepper */}
          <Stepper
            activeStep={activeStep}
            sx={{
              mb: 4,
              '& .MuiStepIcon-root.Mui-active': { color: palette.primary },
              '& .MuiStepIcon-root.Mui-completed': { color: palette.secondary },
              '& .MuiStepLabel-label.Mui-active': { color: palette.textDark, fontWeight: 800 },
              '& .MuiStepLabel-label': { color: palette.textMuted, fontWeight: 600 },
            }}
            alternativeLabel={isMobile}
          >
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Step 1: 確認聯絡方式 */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 900, color: palette.textDark }}>
                {t('apply.step1_desc')}
              </Typography>

              {/* Hidden Google renderButton (triggers popup OAuth) */}
              <Box ref={googleButtonRef} sx={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: 0, overflow: 'hidden' }} />

              {/* Visible custom Google sign-in button */}
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={handleGoogleSignIn}
                disabled={loading}
                sx={{
                  mb: 2,
                  py: 1.5,
                  borderColor: '#4285f4',
                  color: '#4285f4',
                  bgcolor: '#fff',
                  borderRadius: 2.5,
                  fontWeight: 700,
                  fontSize: '1rem',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#357ae8',
                    bgcolor: '#f5f9ff',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 18px rgba(66,133,244,0.16)',
                  }
                }}
                startIcon={
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                }
              >
                {t('apply.google_signup')}
              </Button>

              {!authConfig.google_enabled && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('login.google_not_ready')}
                </Alert>
              )}

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" sx={{ color: palette.textMuted }}>
                  {t('apply.or_email')}
                </Typography>
              </Divider>

              <TextField
                fullWidth
                label={t('apply.email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('apply.email_placeholder')}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: palette.textMuted }} />
                    </InputAdornment>
                  )
                }}
                helperText={t('apply.email_hint')}
              />

              <Button
                fullWidth
                variant="contained"
                size="large"
                data-testid="apply-step1-next"
                onClick={handleNext}
                disabled={!canProceedStep1()}
                sx={{
                  py: 1.5,
                  mt: 2,
                  bgcolor: palette.accent,
                  color: palette.bgDark,
                  fontWeight: 900,
                  borderRadius: 2.5,
                  boxShadow: '0 6px 20px rgba(214,167,91,0.25)',
                  '&:hover': { bgcolor: '#c4963e' },
                  '&.Mui-disabled': { bgcolor: '#e0d8cb', color: '#a09888' },
                }}
              >
                {t('apply.next')}
              </Button>
            </Box>
          )}

          {/* Step 2: Store info */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 1, fontWeight: 900, color: palette.textDark }}>
                {t('apply.step2_desc')}
              </Typography>
              <Typography variant="body2" sx={{ color: palette.textMuted, mb: 3, lineHeight: 1.7 }}>
                {t('apply.step2_subtitle', '填好以下資訊後按送出，系統會在 1-2 分鐘內自動幫你建好商店。')}
              </Typography>

              {/* 1. 選擇方案 */}
              {activationInfo ? (
                <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircleIcon />}>
                  <Typography sx={{ fontWeight: 700 }}>
                    已綁定開通碼：{activationInfo.plan_name}（{activationInfo.duration_months} 個月）
                  </Typography>
                </Alert>
              ) : (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, color: palette.textDark, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RocketIcon sx={{ fontSize: 20, color: palette.accent }} />
                    選擇開店方案（年繳方案專屬免收系統開通費）
                  </Typography>
                  <Grid container spacing={1.5}>
                    {PLAN_OPTIONS.map((plan) => {
                      const isSelected = plan.id === chosenPlanId;
                      return (
                        <Grid item xs={12} sm={6} key={plan.id}>
                          <Paper
                            variant="outlined"
                            onClick={() => setChosenPlanId(plan.id)}
                            sx={{
                              p: 2,
                              height: '100%',
                              borderRadius: 3,
                              cursor: 'pointer',
                              position: 'relative',
                              transition: 'all 0.2s ease',
                              borderColor: isSelected ? palette.accent : palette.border,
                              borderWidth: isSelected ? 2 : 1,
                              bgcolor: isSelected ? `${palette.accent}08` : palette.bgCard,
                              boxShadow: isSelected ? '0 6px 20px rgba(196,154,60,0.15)' : 'none',
                              '&:hover': {
                                borderColor: palette.accent,
                                transform: 'translateY(-2px)',
                              }
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                              <Box>
                                <Chip
                                  label={plan.badge}
                                  size="small"
                                  sx={{
                                    bgcolor: `${plan.badgeColor}18`,
                                    color: plan.badgeColor,
                                    fontWeight: 800,
                                    fontSize: '0.75rem',
                                    mb: 0.8
                                  }}
                                />
                                <Typography variant="subtitle1" sx={{ fontWeight: 900, color: palette.textDark }}>
                                  {plan.name}
                                </Typography>
                              </Box>
                              <Radio
                                checked={isSelected}
                                value={plan.id}
                                sx={{
                                  p: 0.5,
                                  color: palette.border,
                                  '&.Mui-checked': { color: palette.accent },
                                }}
                              />
                            </Stack>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: isSelected ? palette.primary : palette.textDark }}>
                              {plan.id === 1 ? 'NT$ 0' : `NT$ ${plan.yearlyPrice.toLocaleString()}`}
                              <Typography component="span" variant="caption" sx={{ color: palette.textMuted, ml: 0.5 }}>
                                {plan.id === 1 ? '/ 14 天' : '/ 年'}
                              </Typography>
                            </Typography>
                            {plan.id !== 1 && (
                              <Typography variant="caption" sx={{ color: palette.secondary, fontWeight: 700, display: 'block', mb: 1 }}>
                                相當於每月 NT$ {plan.monthlyAvg}
                              </Typography>
                            )}
                            <Typography variant="caption" sx={{ color: palette.textMuted, display: 'block', lineHeight: 1.5, mb: 1.5 }}>
                              {plan.desc}
                            </Typography>
                            <Stack spacing={0.5}>
                              {plan.features.map((feat, fidx) => (
                                <Stack key={fidx} direction="row" spacing={0.8} alignItems="center">
                                  <CheckIcon sx={{ fontSize: 14, color: palette.secondary }} />
                                  <Typography variant="caption" sx={{ color: palette.textDark, fontWeight: 600 }}>
                                    {feat}
                                  </Typography>
                                </Stack>
                              ))}
                            </Stack>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}

              {/* 2. 付款方式 (付費方案時顯示) */}
              {chosenPlanId !== 1 && !activationInfo && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, color: palette.textDark, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CreditCardIcon sx={{ fontSize: 20, color: palette.secondary }} />
                    選擇付款方式（昀美官方 SmilePay 金流收款）
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Paper
                        variant="outlined"
                        onClick={() => setPaymentType('credit_card')}
                        sx={{
                          p: 2,
                          borderRadius: 2.5,
                          cursor: 'pointer',
                          borderColor: paymentType === 'credit_card' ? palette.secondary : palette.border,
                          borderWidth: paymentType === 'credit_card' ? 2 : 1,
                          bgcolor: paymentType === 'credit_card' ? `${palette.secondary}08` : palette.bgCard,
                          transition: 'all 0.2s ease',
                          '&:hover': { borderColor: palette.secondary }
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <CreditCardIcon sx={{ color: palette.secondary, fontSize: 28 }} />
                            <Box>
                              <Typography sx={{ fontWeight: 900, color: palette.textDark }}>
                                信用卡線上刷卡
                              </Typography>
                              <Typography variant="caption" sx={{ color: palette.textMuted }}>
                                支援 Visa / Master / JCB，即刷即自動開通
                              </Typography>
                            </Box>
                          </Stack>
                          <Radio checked={paymentType === 'credit_card'} sx={{ '&.Mui-checked': { color: palette.secondary } }} />
                        </Stack>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper
                        variant="outlined"
                        onClick={() => setPaymentType('atm')}
                        sx={{
                          p: 2,
                          borderRadius: 2.5,
                          cursor: 'pointer',
                          borderColor: paymentType === 'atm' ? palette.secondary : palette.border,
                          borderWidth: paymentType === 'atm' ? 2 : 1,
                          bgcolor: paymentType === 'atm' ? `${palette.secondary}08` : palette.bgCard,
                          transition: 'all 0.2s ease',
                          '&:hover': { borderColor: palette.secondary }
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <BankIcon sx={{ color: palette.secondary, fontSize: 28 }} />
                            <Box>
                              <Typography sx={{ fontWeight: 900, color: palette.textDark }}>
                                ATM 虛擬帳號轉帳
                              </Typography>
                              <Typography variant="caption" sx={{ color: palette.textMuted }}>
                                各大銀行網銀/實體 ATM，入帳秒開通
                              </Typography>
                            </Box>
                          </Stack>
                          <Radio checked={paymentType === 'atm'} sx={{ '&.Mui-checked': { color: palette.secondary } }} />
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* 費用明細小卡 */}
                  <Paper
                    variant="outlined"
                    sx={{
                      mt: 2,
                      p: 2,
                      borderRadius: 2.5,
                      borderColor: `${palette.primary}20`,
                      bgcolor: `${palette.primary}04`
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: palette.textMuted }}>
                        {(PLAN_OPTIONS.find(p => p.id === chosenPlanId) || PLAN_OPTIONS[0]).name}（年繳 12 個月 / 365 天）
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: palette.textDark }}>
                        NT$ {(PLAN_OPTIONS.find(p => p.id === chosenPlanId) || PLAN_OPTIONS[0]).yearlyPrice.toLocaleString()}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" sx={{ color: palette.textMuted }}>
                        系統建置與網域開通費
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#10B981' }}>
                        NT$ 0（年繳專屬免開通費優惠）
                      </Typography>
                    </Stack>
                    <Divider sx={{ my: 1 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography sx={{ fontWeight: 900, color: palette.textDark }}>
                        應付總額
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 900, color: palette.primary }}>
                        NT$ {(PLAN_OPTIONS.find(p => p.id === chosenPlanId) || PLAN_OPTIONS[0]).yearlyPrice.toLocaleString()}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 1 }}>
                      <SecurityIcon sx={{ fontSize: 15, color: palette.secondary }} />
                      <Typography variant="caption" sx={{ color: palette.textMuted }}>
                        由【昀美（SmilePay 速買配，商店代號 4812）】提供 256-bit SSL 安全交易認證
                      </Typography>
                    </Stack>
                  </Paper>
                </Box>
              )}

              {/* 模板選擇 */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 2.5,
                  borderColor: palette.border,
                  bgcolor: palette.bgCard,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box
                      component="img"
                      src={(TEMPLATE_DEFS.find((td) => td.id === templateChoice) || TEMPLATE_DEFS[0]).image}
                      alt={templateChoice}
                      sx={{ width: 64, height: 44, objectFit: 'cover', borderRadius: 1.5, border: `1px solid ${palette.border}` }}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ color: palette.textMuted }}>
                        {t('apply.template')}
                      </Typography>
                      <Typography sx={{ fontWeight: 900, lineHeight: 1.2, color: palette.textDark }} noWrap>
                        {t(`apply.template_${templateChoice}_name`, templateChoice)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: palette.textMuted, display: 'block' }}>
                        {t('apply.template_hint')}
                      </Typography>
                    </Box>
                  </Stack>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowTemplatePicker(true)}
                    data-testid="apply-template-change"
                    sx={{
                      fontWeight: 900,
                      borderRadius: 2,
                      flexShrink: 0,
                      borderColor: palette.border,
                      color: palette.primary,
                      '&:hover': { borderColor: palette.primary },
                    }}
                  >
                    {t('apply.change_template')}
                  </Button>
                </Stack>
              </Paper>

              <TextField
                fullWidth
                label={t('apply.store_name')}
                name="platformName"
                value={formData.platformName}
                onChange={handleChange}
                placeholder={t('apply.store_name_placeholder')}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <StoreIcon sx={{ color: palette.textMuted }} />
                    </InputAdornment>
                  )
                }}
                helperText={t('apply.store_name_hint')}
              />

              <TextField
                fullWidth
                label={t('apply.slug')}
                name="subdomain"
                value={formData.subdomain}
                onChange={handleSubdomainChange}
                placeholder="mystore"
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography color="text.secondary" sx={{ mr: 1 }}>
                        .urstore.tw
                      </Typography>
                      {subdomainStatus.checking && <CircularProgress size={20} />}
                      {!subdomainStatus.checking && subdomainStatus.available === true && (
                        <CheckIcon color="success" />
                      )}
                      {!subdomainStatus.checking && subdomainStatus.available === false && (
                        <CloseIcon color="error" />
                      )}
                    </InputAdornment>
                  )
                }}
                error={subdomainStatus.available === false}
                helperText={
                  subdomainStatus.reason ||
                  (subdomainHint && !formData.subdomain ? subdomainHint :
                  (formData.subdomain.length >= 3 && subdomainStatus.available === true
                    ? t('apply.slug_available', { slug: formData.subdomain })
                    : t('apply.slug_hint')))
                }
              />

              {/* Preview */}
              {formData.platformName && formData.subdomain && subdomainStatus.available && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: `${palette.secondary}08`,
                    borderColor: `${palette.secondary}30`,
                    borderRadius: 2.5,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ color: palette.secondary, fontWeight: 900 }} gutterBottom>
                    {t('apply.store_preview')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 900, color: palette.textDark }}>
                    {formData.platformName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: palette.primary }}>
                    https://{formData.subdomain}.urstore.tw
                  </Typography>
                </Paper>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    name="agreedTerms"
                    checked={formData.agreedTerms}
                    onChange={handleChange}
                    sx={{
                      color: palette.border,
                      '&.Mui-checked': { color: palette.secondary },
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    {t('apply.agree_terms')}{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: palette.primary }}>
                      {t('apply.terms')}
                    </a>
                    {' '}&{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: palette.primary }}>
                      {t('apply.privacy')}
                    </a>
                  </Typography>
                }
                sx={{ mb: 2 }}
              />

              {/* Actions */}
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  sx={{
                    flex: 1,
                    borderColor: palette.border,
                    color: palette.primary,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    '&:hover': { borderColor: palette.primary },
                  }}
                >
                  {t('apply.back')}
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSubmit}
                  disabled={!canProceedStep2() || loading || redirectingToSmilePay}
                  sx={{
                    flex: 2,
                    py: 1.5,
                    bgcolor: palette.accent,
                    color: palette.bgDark,
                    fontWeight: 900,
                    borderRadius: 2.5,
                    boxShadow: '0 6px 20px rgba(214,167,91,0.25)',
                    '&:hover': { bgcolor: '#c4963e' },
                    '&.Mui-disabled': { bgcolor: '#e0d8cb', color: '#a09888' },
                  }}
                >
                  {redirectingToSmilePay ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                      正在前往 SmilePay 安全刷卡頁...
                    </>
                  ) : loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                      {t('apply.submitting')}
                    </>
                  ) : chosenPlanId !== 1 && !activationInfo ? (
                    paymentType === 'credit_card'
                      ? `前往信用卡安全付款（NT$ ${(PLAN_OPTIONS.find(p => p.id === chosenPlanId) || PLAN_OPTIONS[0]).yearlyPrice.toLocaleString()}）`
                      : `取得 ATM 轉帳帳號開通（NT$ ${(PLAN_OPTIONS.find(p => p.id === chosenPlanId) || PLAN_OPTIONS[0]).yearlyPrice.toLocaleString()}）`
                  ) : (
                    t('apply.submit')
                  )}
                </Button>
              </Stack>

              {/* Template picker overlay */}
              {showTemplatePicker && (
                <Box
                  sx={{
                    position: 'fixed',
                    inset: 0,
                    bgcolor: 'rgba(0,0,0,0.55)',
                    zIndex: 2000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                  }}
                  onClick={() => setShowTemplatePicker(false)}
                >
                  <Paper
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      width: '100%',
                      maxWidth: 760,
                      maxHeight: '85vh',
                      overflow: 'auto',
                      p: 2.5,
                      borderRadius: 4,
                      border: `1px solid ${palette.border}`,
                      boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 950, color: palette.textDark }}>
                          {t('apply.template_picker_title')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: palette.textMuted }}>
                          {t('apply.template_picker_hint')}
                        </Typography>
                      </Box>
                      <Button onClick={() => setShowTemplatePicker(false)} sx={{ fontWeight: 900, color: palette.textMuted }}>
                        {t('btn.close', { ns: 'common' })}
                      </Button>
                    </Stack>

                    <Stack spacing={1.5}>
                      {TEMPLATE_DEFS.map((td) => {
                        const selected = td.id === templateChoice;
                        return (
                          <Paper
                            key={td.id}
                            variant="outlined"
                            onClick={() => {
                              setTemplateChoice(td.id);
                              setTemplateInUrl(td.id);
                              setShowTemplatePicker(false);
                            }}
                            data-testid={`apply-template-${td.id}`}
                            sx={{
                              p: 1.6,
                              borderRadius: 2.5,
                              cursor: 'pointer',
                              borderColor: selected ? palette.primary : palette.border,
                              bgcolor: selected ? `${palette.primary}06` : palette.bgCard,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                                borderColor: palette.primary,
                              },
                            }}
                          >
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Box
                                component="img"
                                src={td.image}
                                alt={t(`apply.template_${td.id}_name`, td.id)}
                                sx={{ width: 96, height: 64, objectFit: 'cover', borderRadius: 1.5, border: `1px solid ${palette.border}` }}
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography sx={{ fontWeight: 950, color: palette.textDark }} noWrap>
                                    {t(`apply.template_${td.id}_name`, td.id)}
                                  </Typography>
                                  {selected && (
                                    <Chip
                                      label={t('apply.template_selected')}
                                      size="small"
                                      sx={{
                                        fontWeight: 900,
                                        bgcolor: palette.primary,
                                        color: 'white',
                                      }}
                                    />
                                  )}
                                </Stack>
                                <Typography variant="body2" sx={{ color: palette.textMuted, mt: 0.3 }}>
                                  {t(`apply.template_${td.id}_desc`, '')}
                                </Typography>
                              </Box>
                              <Button
                                variant={selected ? 'contained' : 'outlined'}
                                size="small"
                                sx={{
                                  fontWeight: 900,
                                  borderRadius: 2,
                                  ...(selected
                                    ? { bgcolor: palette.primary, '&:hover': { bgcolor: palette.primary } }
                                    : { borderColor: palette.border, color: palette.primary, '&:hover': { borderColor: palette.primary } }),
                                }}
                              >
                                {selected ? t('apply.template_in_use') : t('apply.template_select')}
                              </Button>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Paper>
                </Box>
              )}
            </Box>
          )}

          {/* Footer hint */}
          <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${palette.border}`, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: palette.textMuted, lineHeight: 1.7 }}>
              {t('apply.footer_hint')}
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default Apply;
